"use client"

import { useState, useEffect } from "react"
import { Calendar, MapPin } from 'lucide-react'
import { Link } from "@tanstack/react-router"
import { cn, Image, Badge } from "@city-sage/aurum-avis-labs-components"
import { Itinerary } from "@city-sage/core-client"
import { Avatar } from "./Avatar"
import { useReactions } from "../lib/useReactions"
import { LikeButton } from "./actions/LikeButton"

interface ItineraryCardProps {
  itinerary: Itinerary
  link: string
  images?: string[]
  showStateBadge?: boolean
}

export function ItineraryCard({ itinerary, link, images = [], showStateBadge = false }: ItineraryCardProps) {
  const [currentImageIndex, setCurrentImageIndex] = useState(0)
  const [progress, setProgress] = useState(0)
  
  const { isLiked, likeCount, toggleLike, isPending } = useReactions({
    reactableType: 'itinerary',
    reactableId: itinerary.id
  })

  // Use images prop if available, otherwise fall back to main itinerary image
  const displayImages = images && images.length > 0 
    ? images.filter(Boolean)
    : itinerary.imageUrl 
      ? [itinerary.imageUrl] 
      : []
  
  const currentImage = displayImages[currentImageIndex] || itinerary.imageUrl || ''

  useEffect(() => {
    if (displayImages.length <= 1) return

    const interval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          setCurrentImageIndex((prevIndex) => 
            (prevIndex + 1) % displayImages.length
          )
          return 0
        }
        return prev + (100 / 40) // 40 steps over 4 seconds
      })
    }, 100)

    return () => clearInterval(interval)
  }, [displayImages.length, currentImageIndex])

  useEffect(() => {
    setProgress(0)
  }, [currentImageIndex])

  const getStateBadgeVariant = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'default'
      case 'PRIVATE':
        case 'DRAFT':
      return 'secondary'
      default:
        return 'secondary'
    }
  }

  const getStateBadgeText = (visibility: string) => {
    switch (visibility) {
      case 'PUBLIC':
        return 'Public'
      case 'PRIVATE':
        return 'Private'
      case 'DRAFT':
        return 'Draft'
      default:
        return visibility
    }
  }

  return (
    <Link to={link} className="block group" resetScroll>
      <article className="overflow-hidden rounded-lg border border-border bg-background transition-all hover:shadow-md">
        {/* Image Section */}
        <div className="relative aspect-[4/3] overflow-hidden bg-muted">
          {/* Progress bars for multiple images */}
          {displayImages.length > 1 && (
            <div className="absolute top-3 left-3 right-3 z-20 flex gap-1.5">
              {displayImages.map((_, index) => (
                <div
                  key={index}
                  className="flex-1 h-0.5 bg-white/40 rounded-full overflow-hidden backdrop-blur-sm"
                >
                  <div
                    className={cn(
                      "h-full bg-white transition-all duration-100 ease-linear rounded-full",
                      index < currentImageIndex && "w-full",
                      index === currentImageIndex && "transition-none",
                      index > currentImageIndex && "w-0"
                    )}
                    style={{
                      width: index === currentImageIndex ? `${progress}%` : 
                             index < currentImageIndex ? '100%' : '0%'
                    }}
                  />
                </div>
              ))}
            </div>
          )}

          {/* State Badge */}
          {showStateBadge && (
            <div className="absolute top-3 right-3 z-20">
              <Badge variant={getStateBadgeVariant(itinerary.visibility)} className="shadow-sm">
                {getStateBadgeText(itinerary.visibility)}
              </Badge>
            </div>
          )}

          {/* Image */}
          {currentImage ? (
            <Image
              src={currentImage || "/placeholder.svg"}
              image={itinerary.image}
              alt={itinerary.title}
              className="w-full h-full"
              imageClassName="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
            />
          ) : (
            <div className="w-full h-full bg-muted flex items-center justify-center">
              <span className="text-muted-foreground">No image</span>
            </div>
          )}
        </div>

        {/* Content Section */}
        <div className="p-5 space-y-4">
          {/* Title */}
          <h3 className="text-xl font-semibold leading-tight text-balance line-clamp-2">
            {itinerary.title}
          </h3>

          {/* Metadata Row */}
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            {itinerary.daysCount !== undefined && itinerary.daysCount > 0 && (
              <div className="flex items-center gap-1.5">
                <Calendar className="h-3.5 w-3.5" />
                <span>{itinerary.daysCount} {itinerary.daysCount === 1 ? 'day' : 'days'}</span>
              </div>
            )}
            {itinerary.stopsCount !== undefined && itinerary.stopsCount > 0 && (
              <div className="flex items-center gap-1.5">
                <MapPin className="h-3.5 w-3.5" />
                <span>{itinerary.stopsCount} stops</span>
              </div>
            )}
          </div>

          {/* Footer Row */}
          <div className="flex items-center justify-between pt-2 border-t">
            {/* Like Button */}
            <div onClick={(e) => e.stopPropagation()}>
              <LikeButton
                isLiked={isLiked}
                likeCount={likeCount}
                onToggleLike={toggleLike}
                isPending={isPending}
                theme="light"
                layout="horizontal"
              />
            </div>

            {/* Author */}
            {itinerary.account && (
              <Link 
                to="/users/$userId" 
                params={{ userId: itinerary.account.id }}
                className="flex items-center gap-2 hover:opacity-70 transition-opacity"
                onClick={(e) => e.stopPropagation()}
              >
                <span className="text-sm text-muted-foreground">
                  @{itinerary.account.userName || 'Anonymous'}
                </span>
                <Avatar className="h-6 w-6" textClassName="text-xs" account={itinerary.account} />
              </Link>
            )}
          </div>
        </div>
      </article>
    </Link>
  )
}
